import { TTheme } from '@/core/theme'
import { CustomTable, CustomTableProps } from '@atbay/cyber-components'
import Typography from '@ui/components/typography'
import { styled } from '@mui/system'
import { ComponentType } from 'react'

export const StyledCustomTable = styled(
    CustomTable as ComponentType<CustomTableProps>
)<{
    theme?: TTheme
}>`
    background-color: ${({ theme }) =>
        theme.colors.semantic.backgroundSecondary};

    /* Always-visible scrollbar (when content overflows) for every table. Styling
       ::-webkit-scrollbar opts out of macOS's auto-hiding overlay scrollbar, and
       overflow:auto keeps it from showing when the rows already fit. */
    .scrollable-parent {
        &::-webkit-scrollbar {
            width: 6px;
            height: 6px;
        }
        &::-webkit-scrollbar-track {
            background: transparent;
        }
        &::-webkit-scrollbar-thumb {
            background-color: ${({ theme }) => theme.colors.greys[200]};
            border-radius: 3px;
        }
    }

    .virtualized-body-wrapper > table tr > td {
        border-top: 1px solid transparent;
        border-bottom: 1px solid transparent;
        border-left: none;
        border-right: none;
    }

    .virtualized-body-wrapper > table tr > td:first-child {
        border-left: 1px solid transparent;
        border-radius: 4px 0 0 4px;
    }

    .virtualized-body-wrapper > table tr > td:last-child {
        border-right: 1px solid transparent;
        border-radius: 0 4px 4px 0;
    }

    ${({ activeRow, theme }) =>
        activeRow !== undefined &&
        `
        .virtualized-body-wrapper > table[data-active="true"] tr > td {
            background-color: ${theme.colors.semantic.tableActive};
            border-top: 1px solid ${theme.colors.semantic.tableBorder};
            border-bottom: 1px solid ${theme.colors.semantic.tableBorder};
            border-left: none;
            border-right: none;
        }
        .virtualized-body-wrapper > table[data-active="true"] tr > td:first-child {
            border-left: 1px solid ${theme.colors.semantic.tableBorder};
            border-radius: 4px 0 0 4px;
        }
        .virtualized-body-wrapper > table[data-active="true"] tr > td:last-child {
            border-right: 1px solid ${theme.colors.semantic.tableBorder};
            border-radius: 0 4px 4px 0;
        }
        
    `}

    table {
        thead {
            color: ${({ theme }) => theme.colors.semantic.tableHeaderText};
        }
        .table-cell-text {
            font-size: var(--font-size-s) !important;
        }

        td,
        th {
            padding-left: 8px;
            padding-right: 8px;
        }

        td:first-child {
            padding-left: 15px;
            padding-right: 8px;
        }

        td:last-child,
        th:last-child {
            padding-left: 8px;
            padding-right: 0;
        }
        border-bottom: 1px solid ${({ theme }) => theme.colors.greys[100]};
        border-spacing: 0;
        border-radius: 8px;

        tr {
            &:hover {
                background-color: ${({ theme }) =>
                    theme.colors.semantic.tableHover};
                border-radius: 12px;
            }
        }

        th:first-child {
            border-radius: 12px 0 0 0;
            padding-left: 20px;
            padding-right: 8px;
        }
        th:last-child {
            border-radius: 0 12px 0 0;
        }

        th {
            height: 45px;
            vertical-align: middle;
            background-color: ${({ theme }) =>
                theme.colors.semantic.tableHeader};
            border-bottom: ${({ theme }) => theme.colors.primary[50]} 1px solid;
        }
    }

    .virtualized-body-wrapper > table:last-child {
        ${({ activeRow }) =>
            !activeRow &&
            `
            border-bottom: none;
        `}
    }

    .empty-state {
        background-color: ${({ theme }) =>
            theme.colors.semantic.backgroundSecondary};
    }

    .css-4m4pg5 {
        background-color: ${({ theme }) => theme.colors.semantic.background};
        border-radius: 12px;
    }
`

export const StyledTableHeader = styled(Typography)`
    margin: 12px 0;
    font-size: var(--font-size-xs);
`

export const TableWrapper = styled('div')<{ theme?: TTheme }>`
    padding: 20px;
    margin: 18px 0 32px 0;
    background-color: ${({ theme }) =>
        theme.colors.semantic.backgroundSecondary};
    border-radius: 16px;
`

export const TableTitleWrapper = styled('div')<{ theme?: TTheme }>`
    display: flex;
    gap: 20px;
`
